import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, RotateCcw, Ban } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { toast } from "./Toast.jsx";
import { confirmDialog } from "./confirmHost.jsx";

// Návrh na propadnutí tréninků kvůli faktuře po splatnosti (>3 dny). Detekci dělá
// denní faktury job; tady owner/CSM potvrdí / zamítne, případně vrátí bonusy.
// Zdroj: /api/klient-propadnuti-faktury (GET seznam, POST confirm|reject|restore-bonus).
export default function PropadnutiFakturySection({ klientName, canManage }) {
  const [navrhy, setNavrhy] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!klientName) return;
    apiFetch(`/api/klient-propadnuti-faktury?klient=${encodeURIComponent(klientName)}`)
      .then((r) => r.json())
      .then((j) => { if (j && j.ok) setNavrhy(j.navrhy || []); })
      .catch(() => {});
  }, [klientName]);
  useEffect(() => { load(); }, [load]);

  if (!navrhy || navrhy.length === 0) return null;

  const aktivni = navrhy.filter((n) => n.stav === "navrzeno");
  const potvrzena = navrhy.filter((n) => n.stav === "potvrzeno");
  if (aktivni.length === 0 && potvrzena.length === 0) return null;

  const fmtDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("cs-CZ"); } catch (_) { return String(d).substring(0, 10); }
  };
  const typLabel = (bt) => (bt === "quarterly" ? "permice" : "koučink");

  const post = async (action, id, dialog, successMsg) => {
    if (busy) return;
    const ok = await confirmDialog(dialog);
    if (!ok) return;
    setBusy(true);
    try {
      const r = await apiFetch("/api/klient-propadnuti-faktury", {
        method: "POST", body: JSON.stringify({ action, id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.error) throw new Error(j.error || "Nepodařilo se provést akci");
      toast(successMsg, "success");
      load();
    } catch (e) { toast("Chyba: " + e.message, "error"); }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      {/* Aktivní návrhy — červený panel s akcemi */}
      {aktivni.map((n) => (
        <div key={n.id} className="rounded-xl border border-rose-300 bg-rose-50 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-rose-800">
                Faktura {n.overdue_days} dní po splatnosti: návrh na propadnutí tréninků
              </div>
              <div className="text-xs text-rose-700 mt-1">
                Faktura {n.invoice_number} ({typLabel(n.billing_type)}), splatnost {fmtDate(n.due_date)}.
                Po potvrzení se <b>vynuluje postup k dalšímu bonusu</b> za spojené tréninky (nevratné, už připsané bonusy zůstávají)
                a <b>odečtou bonusové tréninky</b> z aktuálního zůstatku (nikdy do mínusu). Bonusy lze později ručně vrátit.
              </div>
              {canManage ? (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <button type="button" disabled={busy}
                    onClick={() => post("confirm", n.id, {
                      title: "Potvrdit propadnutí tréninků?",
                      description: `Faktura ${n.invoice_number} je ${n.overdue_days} dní po splatnosti. Vynuluje postup k dalšímu bonusu za spojené tréninky (nevratné) a odečte bonusy ze zůstatku klienta.`,
                      confirmLabel: "Potvrdit propadnutí", variant: "danger",
                    }, "Propadnutí potvrzeno.")}
                    className="btn btn-danger">
                    <Ban className="w-4 h-4" /> Potvrdit propadnutí
                  </button>
                  <button type="button" disabled={busy}
                    onClick={() => post("reject", n.id, {
                      title: "Zamítnout návrh?",
                      description: "Návrh se skryje a tréninky klienta zůstanou beze změny.",
                      confirmLabel: "Zamítnout", variant: "primary",
                    }, "Návrh zamítnut.")}
                    className="btn btn-secondary">
                    Zamítnout
                  </button>
                </div>
              ) : (
                <div className="text-xs text-rose-500 mt-2 italic">Potvrdit nebo zamítnout může owner nebo CSM.</div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Historie — potvrzená propadnutí + možnost vrátit bonusy */}
      {potvrzena.map((n) => (
        <div key={n.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-sm text-slate-700">
            <b>Propadlo {fmtDate(n.resolved_at)}</b>, postup ke spojeným −{n.forfeited_st}, bonusy −{n.forfeited_bonus}
            <span className="text-slate-400">, faktura {n.invoice_number}{n.resolved_by ? `, ${n.resolved_by}` : ""}</span>
          </div>
          {n.bonus_restored_at ? (
            <div className="text-xs text-emerald-600 mt-1">
              Bonusy vráceny {fmtDate(n.bonus_restored_at)}{n.bonus_restored_by ? ` (${n.bonus_restored_by})` : ""}.
            </div>
          ) : (canManage && (Number(n.forfeited_bonus) || 0) > 0 && (
            <button type="button" disabled={busy}
              onClick={() => post("restore-bonus", n.id, {
                title: "Vrátit bonusy zpět?",
                description: `Vrátí ${n.forfeited_bonus} bonusových tréninků zpět do zůstatku klienta. POZOR: postup k dalšímu bonusu za spojené tréninky se NEVRACÍ. Vrací se pouze bonusy.`,
                confirmLabel: "Vrátit bonusy", variant: "primary",
              }, "Bonusy vráceny.")}
              className="btn btn-secondary mt-2">
              <RotateCcw className="w-3.5 h-3.5" /> Vrátit bonusy
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
